import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import { fetchPayrollDetailData } from "../../../redux/payrole-reducers/payroleSlice";
import { getFormattedDate } from "../PayroleMocks";
import { fixedTwo } from "../../common-data/Converts";

const PayrollEditForm = ({
  userInfoData,
  payrollData,
  onClose,
  callFunction,
}) => {
  const dispatch = useDispatch();
  const { payrollDetailData, payrollDetailLoading, payrollDetailError } =
    useSelector((state) => state.payrole);
  const { postPutLoading } = useSelector((state) => state.common);

  const [allData, setAllData] = useState({});

  const initialFormValues = {
    monthly_salary: fixedTwo(allData?.salary_summary?.base_salary || 0),
    esi_amount: fixedTwo(payrollData?.esi_amount || 0),
    incentive: fixedTwo(payrollData?.incentive || 0),
    net_salary: fixedTwo(allData?.salary_summary?.net_salary || 0),
    pf_amount: fixedTwo(payrollData?.pf_amount || 0),
    total_basic_salary: fixedTwo(
      allData?.salary_summary?.gross_salary_sum || 0
    ),
    total_deduction: fixedTwo(allData?.salary_summary?.deduction || 0),
    payout_amount: fixedTwo(allData?.salary_summary?.net_salary || 0),

    total_present: allData?.salary_summary?.full_days || 0,
    total_absent: allData?.salary_summary?.absent_days || 0,
    total_half_day: allData?.salary_summary?.half_days || 0,
    total_leave: 0,
    total_late: 0,
    total_saturday: 0,
    total_sunday: 0,
    total_rostered: 0,
    total_weekoff: 0,
    remarks: "",
  };
  const [formValues, setFormValues] = useState(initialFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [attendanceData, setAttendanceData] = useState({});
  const [currentMonth, setCurrentMonth] = useState(() => {
    return payrollData?.date ? new Date(payrollData.date) : new Date();
  });

  const getPayrollDetail = (filters) => {
    dispatch(
      fetchPayrollDetailData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  };

  const attendanceTypes = [
    { id: "present", label: "Present", color: "#08CB00" },
    { id: "half-day", label: "Half Day", color: "#FF9B00" },
    { id: "absent", label: "Absent", color: "#E43636" },
    { id: "leave", label: "Leave", color: "#FF2DD1" },
    { id: "weekoff", label: "Week Off", color: "#006BFF" },
    { id: "saturday", label: "Saturday", color: "#F7A5A5" },
    { id: "sunday", label: "Sunday", color: "#4A4947" },
    { id: "rostered", label: "Rostered", color: "#6C48C5" },
    { id: "late", label: "Late", color: "#B33791" },
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonthDay = new Date(year, month, -startingDayOfWeek + i + 1);
      days.push({
        date: prevMonthDay.getDate(),
        isCurrentMonth: false,
        fullDate: prevMonthDay,
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(year, month, day);
      days.push({
        date: day,
        isCurrentMonth: true,
        fullDate: dayDate,
      });
    }

    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      const nextMonthDay = new Date(year, month + 1, i);
      days.push({
        date: nextMonthDay.getDate(),
        isCurrentMonth: false,
        fullDate: nextMonthDay,
      });
    }

    return days;
  };

  const handleInputChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAttendanceChange = (date, type) => {
    const dateString = moment.tz(date, "Asia/Kolkata").format("YYYY-MM-DD");
    setAttendanceData((prev) => ({
      ...prev,
      [dateString]: type,
    }));
    calculateAttendanceStats();
  };

  const calculateAttendanceStats = () => {
    const stats = {
      total_present: 0,
      total_absent: 0,
      total_half_day: 0,
      total_leave: 0,
      total_weekoff: 0,
      total_saturday: 0,
      total_sunday: 0,
      total_rostered: 0,
      total_late: 0,
    };

    Object.values(attendanceData).forEach((type) => {
      switch (type) {
        case "present":
          stats.total_present++;
          break;
        case "absent":
          stats.total_absent++;
          break;
        case "half-day":
          stats.total_half_day++;
          break;
        case "leave":
          stats.total_leave++;
          break;
        case "weekoff":
          stats.total_weekoff++;
          break;
        case "saturday":
          stats.total_saturday++;
          break;
        case "sunday":
          stats.total_sunday++;
          break;
        case "rostered":
          stats.total_rostered++;
          break;
        case "late":
          stats.total_late++;
          break;
        default:
          break;
      }
    });

    setFormValues((prev) => ({
      ...prev,
      ...stats,
    }));
  };

  const getAttendanceType = (date) => {
    const dateString = moment.tz(date, "Asia/Kolkata").format("YYYY-MM-DD");
    return attendanceData[dateString] || "present";
  };

  const validateForm = (values) => {
    const errors = {};

    if (!values.monthly_salary || values.monthly_salary <= 0) {
      errors.monthly_salary = "Monthly salary must be greater than 0!";
    }

    if (values.esi_amount < 0) {
      errors.esi_amount = "ESI amount cannot be negative!";
    }

    if (values.pf_amount < 0) {
      errors.pf_amount = "PF amount cannot be negative!";
    }

    if (values.incentive < 0) {
      errors.incentive = "Incentive cannot be negative!";
    }

    if (values.net_salary < 0) {
      errors.net_salary = "Net salary cannot be negative!";
    }

    if (values.total_basic_salary < 0) {
      errors.total_basic_salary = "Total basic salary cannot be negative!";
    }

    if (values.total_deduction < 0) {
      errors.total_deduction = "Total deduction cannot be negative!";
    }

    // if (values.total_weekoff < 0) {
    //   errors.total_weekoff = "Week off days cannot be negative!";
    // }

    if (values.payout_amount < 0) {
      errors.payout_amount = "Payout amount cannot be negative!";
    }

    return errors;
  };

  const handleSubmitForm = async (payload) => {
    const method = "PUT";
    const endpoint = "/payrole/update";
    const token = userInfoData?.token || "";

    try {
      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
        callFunction();
        toast.success(
          result?.payload?.message || "Payroll updated successfully!"
        );
        onClose();
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error updating payroll:", error);
      toast.error("Failed to update payroll. Please try again.");
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    // const errors = validateForm(formValues);
    // setFormErrors(errors);

    // if (Object.keys(errors).length === 0) {
    const payload = {
      payrole_id: payrollData?.uid || "",
      // monthly_salary: parseFloat(formValues.monthly_salary),
      // esi_amount: parseFloat(formValues.esi_amount),
      incentive: parseFloat(formValues.incentive),
      // net_salary: parseFloat(formValues.net_salary),
      // pf_amount: parseFloat(formValues.pf_amount),
      // total_basic_salary: parseFloat(formValues.total_basic_salary),
      // total_deduction: parseFloat(formValues.total_deduction),
      // payout_amount: parseFloat(formValues.payout_amount),
      total_present: parseInt(formValues.total_present),
      total_absent: parseInt(formValues.total_absent),
      total_half_day: parseInt(formValues.total_half_day),
      total_leave: parseInt(formValues.total_leave),
      total_late: parseInt(formValues.total_late),
      total_weekoff: parseInt(formValues.total_weekoff),
      total_saturday: parseInt(formValues.total_saturday),
      total_sunday: parseInt(formValues.total_sunday),
      total_rostered: parseInt(formValues.total_rostered),
      attendance_data: Object.entries(attendanceData).map(([date, status]) => ({
        date: date,
        status: status,
      })),
      remarks: formValues.remarks,
    };

    const handleConfirm = async () => {
      handleSubmitForm(payload);
    };

    showConfirmAlert(
      handleConfirm,
      `Are you sure you want to update the payroll details for ${payrollData?.name}? This action cannot be undone.`,
      "No, Cancel",
      "Yes, Update Payroll"
    );
    // }
  };

  useEffect(() => {
    if (payrollData?.user_id && payrollData?.date) {
      const sendableFilters = {
        user_id: payrollData.user_id,
        month: getFormattedDate(payrollData),
      };

      getPayrollDetail(sendableFilters);
    }
  }, []);

  useEffect(() => {
    if (
      !payrollDetailLoading &&
      !payrollDetailError &&
      payrollDetailData?.status &&
      payrollDetailData?.data
    ) {
      setAllData(payrollDetailData.data);
    } else {
      setAllData({});
    }
  }, [payrollDetailLoading, payrollDetailError, payrollDetailData]);

  useEffect(() => {
    if (allData?.calander && allData.calander.length > 0) {
      const calendarAttendanceData = {};

      allData.calander.forEach((day) => {
        const dateString = moment
          .tz(day.date, "Asia/Kolkata")
          .format("YYYY-MM-DD");
        let attendanceType = "present";

        if (day.is_leave_day) {
          attendanceType = "leave";
        } else if (day.is_weekoff) {
          attendanceType = "weekoff";
        } else if (day.is_sunday) {
          attendanceType = "sunday";
        } else if (day.is_saturday) {
          attendanceType = "saturday";
        } else if (day.is_rostered_day) {
          attendanceType = "rostered";
        } else if (day.is_half_day) {
          attendanceType = "half-day";
        } else if (day.is_late) {
          attendanceType = "late";
        } else if (day.is_absent) {
          attendanceType = "absent";
        } else if (day.is_present) {
          attendanceType = "present";
        }

        calendarAttendanceData[dateString] = attendanceType;
      });

      setAttendanceData(calendarAttendanceData);

      const stats = {
        total_present: 0,
        total_absent: 0,
        total_half_day: 0,
        total_leave: 0,
        total_weekoff: 0,
        total_saturday: 0,
        total_sunday: 0,
        total_rostered: 0,
        total_late: 0,
      };

      Object.values(calendarAttendanceData).forEach((type) => {
        switch (type) {
          case "present":
            stats.total_present++;
            break;
          case "absent":
            stats.total_absent++;
            break;
          case "half-day":
            stats.total_half_day++;
            break;
          case "leave":
            stats.total_leave++;
            break;
          case "weekoff":
            stats.total_weekoff++;
            break;
          case "saturday":
            stats.total_saturday++;
            break;
          case "sunday":
            stats.total_sunday++;
            break;
          case "rostered":
            stats.total_rostered++;
            break;
          case "late":
            stats.total_late++;
            break;
          default:
            break;
        }
      });

      setFormValues((prev) => ({
        ...prev,
        ...stats,
        monthly_salary: fixedTwo(
          allData?.salary_summary?.base_salary || prev.monthly_salary
        ),
        net_salary: fixedTwo(
          allData?.salary_summary?.net_salary || prev.net_salary
        ),
        total_basic_salary: fixedTwo(
          allData?.salary_summary?.gross_salary_sum || prev.total_basic_salary
        ),
        total_deduction: fixedTwo(
          allData?.salary_summary?.deduction || prev.total_deduction
        ),
        payout_amount: fixedTwo(
          allData?.salary_summary?.net_salary || prev.payout_amount
        ),

        total_weekoff: allData?.salary_summary?.holidays || prev.total_weekoff,
        total_present:
          allData?.salary_summary?.full_days || stats.total_present,
        total_absent:
          allData?.salary_summary?.absent_days || stats.total_absent,
        total_half_day:
          allData?.salary_summary?.half_days || stats.total_half_day,
      }));
    }
  }, [allData]);

  useEffect(() => {
    if (payrollData?.date) {
      setCurrentMonth(new Date(payrollData.date));
    }
  }, [payrollData?.date]);

  useEffect(() => {
    calculateAttendanceStats();
  }, [attendanceData]);

  console.log("attendanceData=> ", attendanceData);
  console.log("formValues=> ", formValues);

  return (
    <div className="pp-approval-container">
      <div className="pp-approval-overlay" onClick={onClose}></div>
      <form className="pp-approval-form pp-edit-form">
        <div className="pp-approval-header">
          <h3 className="pp-approval-title">Edit Staff Payroll Details</h3>
          <button
            type="button"
            className="pp-approval-close-btn"
            onClick={onClose}
          >
            X
          </button>
        </div>

        <div className="pp-approval-content">
          <div className="pp-approval-employee-card">
            <div className="pp-approval-employee-header">
              <div className="pp-approval-employee-avatar">
                {payrollData?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="pp-approval-employee-info">
                <h4 className="pp-approval-employee-name">
                  {allData?.salary_summary?.name || payrollData?.name}
                </h4>
                <p className="pp-approval-employee-role">
                  {payrollData?.account_type?.replace(/_/g, " ") || "Employee"}
                </p>
                <p className="pp-approval-employee-gym">
                  {payrollData?.gym_name || "Gym"}
                </p>
              </div>
            </div>
          </div>

          <div className="pp-edit-section">
            <div className="pp-edit-instructions">
              <div className="pp-edit-instructions-header">
                <span className="pp-edit-instructions-icon">💰</span>
                <h4 className="pp-edit-instructions-title">
                  Update Staff Salary & Attendance Information
                </h4>
              </div>
              <div className="pp-edit-instructions-content">
                <p className="pp-edit-instructions-text">
                  Update the staff salary details and attendance below. Click on
                  calendar dates to change attendance status. All changes will
                  be reflected in the final payroll calculation.
                </p>
              </div>
            </div>

            <div className="pp-edit-content-wrapper">
              <div className="pp-edit-salary-section">
                <div className="pp-edit-card">
                  <div className="pp-edit-card-header">
                    <h4 className="pp-edit-card-title">
                      💵 Base Salary Information
                    </h4>
                  </div>
                  <div className="pp-edit-card-content">
                    <div className="pp-edit-form-grid">
                      <div className="pp-edit-form-item">
                        <label className="pp-edit-form-label">
                          Monthly Salary{" "}
                          <span className="pp-approval-required">*</span>
                        </label>
                        <input
                          type="number"
                          className={`pp-edit-form-input ${
                            formErrors?.monthly_salary ? "formError" : ""
                          }`}
                          value={formValues.monthly_salary}
                          onChange={(e) =>
                            handleInputChange("monthly_salary", e.target.value)
                          }
                          placeholder="Enter monthly salary"
                          min="0"
                          step="0.01"
                          disabled
                        />
                        {formErrors?.monthly_salary && (
                          <span className="pp-approval-error">
                            {formErrors.monthly_salary}
                          </span>
                        )}
                        <small className="pp-edit-form-help">
                          Base Salary: ₹
                          {fixedTwo(allData?.salary_summary?.base_salary || 0)}
                        </small>
                      </div>

                      <div className="pp-edit-form-item">
                        <label className="pp-edit-form-label">
                          Total Basic Salary
                        </label>
                        <input
                          type="number"
                          className={`pp-edit-form-input ${
                            formErrors?.total_basic_salary ? "formError" : ""
                          }`}
                          value={formValues.total_basic_salary}
                          onChange={(e) =>
                            handleInputChange(
                              "total_basic_salary",
                              e.target.value
                            )
                          }
                          placeholder="Enter total basic salary"
                          min="0"
                          step="0.01"
                          disabled
                        />
                        {formErrors?.total_basic_salary && (
                          <span className="pp-approval-error">
                            {formErrors.total_basic_salary}
                          </span>
                        )}
                        <small className="pp-edit-form-help">
                          Gross Salary: ₹
                          {fixedTwo(
                            allData?.salary_summary?.gross_salary_sum || 0
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pp-edit-card">
                  <div className="pp-edit-card-header">
                    <h4 className="pp-edit-card-title">
                      💳 Deductions & Benefits
                    </h4>
                  </div>
                  <div className="pp-edit-card-content">
                    <div className="pp-edit-form-grid">
                      <div className="pp-edit-form-item">
                        <label className="pp-edit-form-label">ESI Amount</label>
                        <input
                          type="number"
                          className={`pp-edit-form-input ${
                            formErrors?.esi_amount ? "formError" : ""
                          }`}
                          value={formValues.esi_amount}
                          onChange={(e) =>
                            handleInputChange("esi_amount", e.target.value)
                          }
                          placeholder="Enter ESI amount"
                          min="0"
                          step="0.01"
                          disabled
                        />
                        {formErrors?.esi_amount && (
                          <span className="pp-approval-error">
                            {formErrors.esi_amount}
                          </span>
                        )}
                      </div>

                      <div className="pp-edit-form-item">
                        <label className="pp-edit-form-label">PF Amount</label>
                        <input
                          type="number"
                          className={`pp-edit-form-input ${
                            formErrors?.pf_amount ? "formError" : ""
                          }`}
                          value={formValues.pf_amount}
                          onChange={(e) =>
                            handleInputChange("pf_amount", e.target.value)
                          }
                          placeholder="Enter PF amount"
                          min="0"
                          step="0.01"
                          disabled
                        />
                        {formErrors?.pf_amount && (
                          <span className="pp-approval-error">
                            {formErrors.pf_amount}
                          </span>
                        )}
                      </div>

                      <div className="pp-edit-form-item">
                        <label className="pp-edit-form-label">
                          Total Deduction
                        </label>
                        <input
                          type="number"
                          className={`pp-edit-form-input ${
                            formErrors?.total_deduction ? "formError" : ""
                          }`}
                          value={formValues.total_deduction}
                          onChange={(e) =>
                            handleInputChange("total_deduction", e.target.value)
                          }
                          placeholder="Enter total deduction"
                          min="0"
                          step="0.01"
                          disabled
                        />
                        {formErrors?.total_deduction && (
                          <span className="pp-approval-error">
                            {formErrors.total_deduction}
                          </span>
                        )}
                      </div>

                      <div className="pp-edit-form-item">
                        <label className="pp-edit-form-label">
                          Incentive Amount
                        </label>
                        <input
                          type="number"
                          className={`pp-edit-form-input ${
                            formErrors?.incentive ? "formError" : ""
                          }`}
                          value={formValues.incentive}
                          onChange={(e) =>
                            handleInputChange("incentive", e.target.value)
                          }
                          placeholder="Enter incentive amount"
                          min="0"
                          step="0.01"
                        />
                        {formErrors?.incentive && (
                          <span className="pp-approval-error">
                            {formErrors.incentive}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pp-edit-card">
                  <div className="pp-edit-card-header">
                    <h4 className="pp-edit-card-title">
                      💸 Final Salary Amounts
                    </h4>
                  </div>
                  <div className="pp-edit-card-content">
                    <div className="pp-edit-form-grid">
                      <div className="pp-edit-form-item">
                        <label className="pp-edit-form-label">Net Salary</label>
                        <input
                          type="number"
                          className={`pp-edit-form-input ${
                            formErrors?.net_salary ? "formError" : ""
                          }`}
                          value={formValues.net_salary}
                          onChange={(e) =>
                            handleInputChange("net_salary", e.target.value)
                          }
                          placeholder="Enter net salary"
                          min="0"
                          step="0.01"
                          disabled
                        />
                        {formErrors?.net_salary && (
                          <span className="pp-approval-error">
                            {formErrors.net_salary}
                          </span>
                        )}
                      </div>

                      <div className="pp-edit-form-item">
                        <label className="pp-edit-form-label">
                          Payout Amount
                        </label>
                        <input
                          type="number"
                          className={`pp-edit-form-input ${
                            formErrors?.payout_amount ? "formError" : ""
                          }`}
                          value={formValues.payout_amount}
                          onChange={(e) =>
                            handleInputChange("payout_amount", e.target.value)
                          }
                          placeholder="Enter payout amount"
                          min="0"
                          step="0.01"
                          disabled
                        />
                        {formErrors?.payout_amount && (
                          <span className="pp-approval-error">
                            {formErrors.payout_amount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pp-edit-attendance-section">
                <div className="pp-edit-card">
                  <div className="pp-edit-card-header">
                    <h4 className="pp-edit-card-title">📅 Update Attendance</h4>
                  </div>
                  <div className="pp-edit-card-content">
                    <div className="pp-edit-calendar-header">
                      <span className="pp-edit-calendar-month">
                        {allData?.salary_summary?.month
                          ? new Date(
                              allData.salary_summary.month
                            ).toLocaleDateString("en-US", {
                              month: "long",
                              year: "numeric",
                            })
                          : currentMonth.toLocaleDateString("en-US", {
                              month: "long",
                              year: "numeric",
                            })}
                      </span>
                    </div>

                    <div className="pp-edit-calendar-grid">
                      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                        <div key={day} className="pp-edit-calendar-weekday">
                          {day}
                        </div>
                      ))}

                      {getDaysInMonth(currentMonth).map((day, index) => {
                        const attendanceType = day.isCurrentMonth
                          ? getAttendanceType(day.fullDate)
                          : "";

                        return (
                          <div
                            key={index}
                            className={`pp-edit-calendar-day ${
                              !day.isCurrentMonth ? "other-month" : ""
                            } ${attendanceType}`}
                            onClick={() =>
                              day.isCurrentMonth &&
                              document
                                .getElementById(
                                  `attendance-${moment
                                    .tz(day.fullDate, "Asia/Kolkata")
                                    .format("YYYY-MM-DD")}`
                                )
                                .click()
                            }
                          >
                            {day.date}
                            {day.isCurrentMonth && (
                              <select
                                id={`attendance-${moment
                                  .tz(day.fullDate, "Asia/Kolkata")
                                  .format("YYYY-MM-DD")}`}
                                className="pp-edit-attendance-select"
                                value={getAttendanceType(day.fullDate)}
                                onChange={(e) =>
                                  handleAttendanceChange(
                                    day.fullDate,
                                    e.target.value
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              >
                                {attendanceTypes.map((type) => (
                                  <option key={type.id} value={type.id}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="pp-edit-attendance-legend">
                      <h5 className="pp-edit-legend-title">
                        Attendance Types:
                      </h5>
                      <div className="pp-edit-legend-grid">
                        {attendanceTypes.map((type) => (
                          <div key={type.id} className="pp-edit-legend-item">
                            <div
                              className="pp-edit-legend-indicator"
                              style={{ backgroundColor: type.color }}
                            ></div>
                            <span className="pp-edit-legend-text">
                              {type.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Attendance Stats */}
                    <div className="pp-edit-attendance-stats">
                      <h5 className="pp-edit-stats-title">
                        Attendance Summary:
                      </h5>
                      <div className="pp-edit-stats-grid">
                        <div className="pp-edit-stat-item">
                          <span className="pp-edit-stat-label">Present:</span>
                          <span className="pp-edit-stat-value">
                            {formValues.total_present}
                          </span>
                        </div>
                        <div className="pp-edit-stat-item">
                          <span className="pp-edit-stat-label">Half Day:</span>
                          <span className="pp-edit-stat-value">
                            {formValues.total_half_day}
                          </span>
                        </div>
                        <div className="pp-edit-stat-item">
                          <span className="pp-edit-stat-label">Absent:</span>
                          <span className="pp-edit-stat-value">
                            {formValues.total_absent}
                          </span>
                        </div>
                        <div className="pp-edit-stat-item">
                          <span className="pp-edit-stat-label">Leave:</span>
                          <span className="pp-edit-stat-value">
                            {formValues.total_leave}
                          </span>
                        </div>
                        <div className="pp-edit-stat-item">
                          <span className="pp-edit-stat-label">Week Off:</span>
                          <span className="pp-edit-stat-value">
                            {formValues.total_weekoff}
                          </span>
                        </div>
                        <div className="pp-edit-stat-item">
                          <span className="pp-edit-stat-label">Saturday:</span>
                          <span className="pp-edit-stat-value">
                            {formValues.total_saturday}
                          </span>
                        </div>
                        <div className="pp-edit-stat-item">
                          <span className="pp-edit-stat-label">Sunday:</span>
                          <span className="pp-edit-stat-value">
                            {formValues.total_sunday}
                          </span>
                        </div>
                        <div className="pp-edit-stat-item">
                          <span className="pp-edit-stat-label">Rostered:</span>
                          <span className="pp-edit-stat-value">
                            {formValues.total_rostered}
                          </span>
                        </div>
                        <div className="pp-edit-stat-item">
                          <span className="pp-edit-stat-label">Late:</span>
                          <span className="pp-edit-stat-value">
                            {formValues.total_late}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Remarks Section */}
          <div className="pp-approval-remarks-section">
            <label htmlFor="remarks" className="pp-approval-section-label">
              Remarks (Optional)
            </label>
            <textarea
              id="remarks"
              name="remarks"
              value={formValues.remarks}
              onChange={(e) => handleInputChange("remarks", e.target.value)}
              placeholder="Enter any additional remarks for this payroll update..."
              className="pp-approval-remarks-textarea"
              rows={4}
              maxLength={500}
            />
            <div className="pp-approval-remarks-footer">
              <span className="pp-approval-remarks-counter">
                {formValues.remarks.length}/500 characters
              </span>
            </div>
          </div>

          <div className="pp-approval-form-actions">
            <button
              type="button"
              className="pp-approval-btn cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="pp-approval-btn submit"
              onClick={onSubmit}
              disabled={postPutLoading}
            >
              {postPutLoading ? "Updating..." : "Update Staff Payroll"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PayrollEditForm;
